import React, { useState } from "react";
import Swal from "sweetalert2";
import { pdf, Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import "./FormularioProceso.css";
import QRCode from "qrcode";
import sescudo from "./escudo";

const FECHA_INGRESO = new Date("2026-03-01");
const escudo = sescudo();

const PROVINCIAS = [
  "Buenos Aires","Catamarca","Chaco","Chubut","Córdoba","Corrientes","Entre Ríos","Formosa","Jujuy","La Pampa",
  "La Rioja","Mendoza","Misiones","Neuquén","Río Negro","Salta","San Juan","San Luis","Santa Cruz","Santa Fe",
  "Santiago del Estero","Tierra del Fuego","Tucumán",
];

const DEPARTAMENTOS_SF = [
  "9 de Julio","Belgrano","Caseros","Castellanos","Constitución","Garay","General López","General Obligado","Iriondo",
  "La Capital","Las Colonias","Rosario","San Cristóbal","San Javier","San Jerónimo","San Justo","San Lorenzo","Vera","San Martín",
];

const PREGUNTAS = [
  "¿Tiene algún cargo de planta permanente en la Administración Pública?",
  "¿Fue destituido, cesanteado o exonerado en cualquiera de los niveles de la Administración Pública?",
  "¿Ha sido condenado por la justicia Provincial o Nacional por delitos o contravenciones?",
  "¿Se encuentra procesado por la justicia Provincial o Nacional?"
];

// 📌 Estilos para PDF
const pdfStyles = StyleSheet.create({
  page: { padding: 20, fontSize: 12 },
  title: { fontSize: 18, marginBottom: 10, fontWeight: "bold" },
  field: { marginBottom: 5 }
});

const FormularioProceso = () => {
  const etapas = [
    ["dni","email","cuil","nombres","apellido","genero","estadoCivil","fechaDeNacimiento"],
    ["argentinoNativo","provinciaNacimiento","lugarDeNacimiento","apellidoNombreMadre","apellidoNombrePadre",
     "resideProvincia","noResideProvincia","departamento","domicilio","localidad","telefono_1","telefono_2","comisaria"],
    ["nivelEstudio","tituloSecundario","fechaTituloSecundario","tituloSuperior","fechaTituloSuperior","tieneBoletoEstudiantil",
     ...PREGUNTAS.map((_, idx) => `pregunta_${idx}`)],
    ["resumen"]
  ];

  const [formData, setFormData] = useState(
    etapas.flat().reduce((acc,campo)=>({...acc,[campo]:campo.includes("pregunta")?"No":""}),{})
  );
  const [etapaActual, setEtapaActual] = useState(0);

  // --- utilidades ---
  const calcularCuil = (dni,genero)=>{
    if(!dni || dni.length<7) return "";
    let prefijo = genero==="Femenino"?"27":genero==="Masculino"?"20":"23";
    let base = prefijo+dni.padStart(8,"0");
    let factores=[5,4,3,2,7,6,5,4,3,2];
    let suma=0;
    for(let i=0;i<10;i++) suma+=parseInt(base[i])*factores[i];
    let resto = suma%11;
    let digito = 11-resto;
    if(digito===11) digito=0;
    else if(digito===10){digito=9; prefijo="23"; base=prefijo+dni.padStart(8,"0");}
    return `${prefijo}-${dni.padStart(8,"0")}-${digito}`;
  };

  const calcularEdad=(fechaNacimiento)=>{
    if(!fechaNacimiento) return null;
    let nacimiento = new Date(fechaNacimiento);
    let edad = FECHA_INGRESO.getFullYear()-nacimiento.getFullYear();
    let m = FECHA_INGRESO.getMonth()-nacimiento.getMonth();
    if(m<0||(m===0 && FECHA_INGRESO.getDate()<nacimiento.getDate())) edad--;
    return edad;
  };

  const validarEdad=()=>{
    const fechaNac = formData.fechaDeNacimiento;
    if(!fechaNac) return true;
    const edad = calcularEdad(fechaNac);
    if(edad<18 || edad>28){
      Swal.fire("Edad no válida",`La edad debe estar entre 18 y 28 años al ${FECHA_INGRESO.toLocaleDateString()}.`,"error");
      return false;
    }
    return true;
  };

  const handleChange=(e)=>{
    let {name,value} = e.target;
    if(name==="dni") value=value.replace(/\D/g,"");
    const updatedData = {...formData,[name]:value};

    if(name==="dni"||name==="genero") updatedData.cuil = calcularCuil(updatedData.dni,updatedData.genero);

    if(name==="resideProvincia"){
      if(value==="Si"){
        updatedData.noResideProvincia="";
      } else {
        updatedData.departamento="";
        updatedData.localidad="";
        updatedData.noResideProvincia="";
      }
    }

    setFormData(updatedData);
  };

  const validarEtapa=()=>{
    if(etapaActual >= etapas.length) return true;
    const campos = etapas[etapaActual];
    for(let campo of campos){
      if(campo==="resumen") continue;
      if(campo==="telefono_2") continue; 
      if(campo==="departamento" && formData["resideProvincia"]!=="Si") continue;
      if(campo==="noResideProvincia" && formData["resideProvincia"]==="Si") continue;
      if(campo==="provinciaNacimiento" && formData["argentinoNativo"]!=="Si") continue;
      if((campo==="tituloSuperior" || campo==="fechaTituloSuperior") && formData.nivelEstudio==="Secundario completo") continue;
      if(!formData[campo]){
        Swal.fire("Atención",`El campo "${formatearLabel(campo)}" es obligatorio.`,"warning");
        return false;
      }
    }
    if(etapaActual===0 && !validarEdad()) return false;
    return true;
  };

  const handleNext=()=>{ if(validarEtapa()) setEtapaActual(etapaActual+1); };
  const handleBack=()=>setEtapaActual(etapaActual-1);

  // --- Formatear label ---
  const formatearLabel = texto=>{
    if(texto.startsWith("pregunta_")){
      const idx = parseInt(texto.split("_")[1]);
      return PREGUNTAS[idx];
    }
    return texto.replace(/_/g," ").replace(/([A-Z])/g," $1").trim().replace(/^\w/,c=>c.toUpperCase());
  };

  // --- Finalizar e imprimir PDF ---
  const handleFinalizar = async (e)=>{
    e.preventDefault();
    if (!validarEtapa()) return;

    const confirmEnvio = await Swal.fire({
      title: "¿Finalizar inscripción?",
      text: "Se enviarán tus datos y no podrás modificarlos.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, enviar",
      cancelButtonText: "Cancelar",
    });

    if (!confirmEnvio.isConfirmed) return;

    // 📌 Procesar datos finales
    const datosFinales = { ...formData };
    // ❌ No reemplazar claves, dejar pregunta_0..3
    PREGUNTAS.forEach((_, idx) => {
      datosFinales[`pregunta_${idx}`] = formData[`pregunta_${idx}`];
    });

    Swal.fire("Éxito", "Formulario enviado correctamente 🎉", "success");
//     console.log("📌 Datos enviados:", datosFinales);

    // 🔹 Función asincrónica opcional
    // await enviarFormularioAPI(datosFinales);

    const confirmLegajo = await Swal.fire({
      title: "¿Desea ver el legajo en PDF?",
      text: "Se abrirá en una nueva pestaña.",
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Sí, mostrar",
      cancelButtonText: "No",
    });

    if (!confirmLegajo.isConfirmed) return;

    const referencia = `PROVISORIO-${datosFinales.dni}`;
    const qr = await QRCode.toDataURL(referencia);

    const doc = (
      <Document>
        <Page size="A4" style={pdfStyles.page}>
          {/* Escudo centrado */}
          {escudo && (
            <Image
              src={escudo}
              style={{ width: 80, height: 80, alignSelf: "center", marginBottom: 10 }}
            />
          )}
              <Text style={{ fontSize: 18, fontWeight: "bold",alignSelf: "center", marginBottom: 4 }}>Ingreso 2026-2027</Text>
              <Text style={{ fontSize: 14, fontWeight: "bold",alignSelf: "center", marginBottom: 4 }}>Segunda convocatoria</Text>
          {[ // Secciones
            { titulo: "Datos Personales", campos: ["dni","email","cuil","nombres","apellido","genero","estadoCivil","fechaDeNacimiento"] },
            { titulo: "Datos de Residencia", campos: ["argentinoNativo","provinciaNacimiento","lugarDeNacimiento","apellidoNombreMadre","apellidoNombrePadre",
                                                      "resideProvincia","noResideProvincia","departamento","domicilio","localidad","telefono_1","telefono_2","comisaria"] },
            { titulo: "Estudios", campos: ["nivelEstudio","tituloSecundario","fechaTituloSecundario","tituloSuperior","fechaTituloSuperior","tieneBoletoEstudiantil"] },
            { titulo: "Preguntas", campos: ["pregunta_0","pregunta_1","pregunta_2","pregunta_3"] }
          ].map((seccion, sIdx) => (
            <View key={sIdx} style={{ marginBottom: 12 }}>
              <Text style={{ fontSize: 12, fontWeight: "bold", marginBottom: 4 }}>{seccion.titulo}</Text>
              <View style={{ borderBottomWidth: 1, borderBottomColor: "#000", marginVertical: 2 }} />
              {seccion.campos.map((key) => datosFinales[key] ? (
                <View key={key} style={{ marginBottom: 2 }}>
                  <Text style={{ fontSize: 9, lineHeight: 1.3 }}>
                    {formatearLabel(key)}: {datosFinales[key]}
                  </Text>
                </View>
              ) : null)}
            </View>
          ))}

        <Text  style={
            {
              textAlign:"center",fontSize:"8px", color:"red"
            }
            }>
           Recuerde que la inscripción se completará una vez entregada la totalidad de la documental requerida, en hora, fecha y sede que se asignará en publicación de la Página Web una vez cerrada la etapa de Pre Inscripción.
         
          </Text>
          {qr && (
            <View style={{ marginTop:1, alignItems: "center" }}>
           
              <Image src={qr} style={{ width: 100, height: 100 }} />
            </View>
          )}
        </Page>
      </Document>
    );

    const blob = await pdf(doc).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const mostrarTituloSuperior = formData.nivelEstudio !== "Secundario completo";
  const progreso = ((etapaActual+1)/etapas.length)*100;

  return (
    <div className="formulario-container">
      <h2 className="formulario-titulo">Formulario de Inscripción</h2>
      <div className="barra-progreso">
        <div className="barra-progreso-inner" style={{width: `${progreso}%`}}></div>
      </div>
      <form className="formulario-grid">

        {etapaActual < etapas.length - 1 && etapas[etapaActual].map(key=>{
          if(key==="noResideProvincia" && formData["resideProvincia"]!=="No") return null;
          if(key==="departamento" && formData["resideProvincia"]!=="Si") return null;
          if(key==="provinciaNacimiento" && formData["argentinoNativo"]!=="Si") return null;
          if((key==="tituloSuperior"||key==="fechaTituloSuperior") && !mostrarTituloSuperior) return null;

          return (
            <div key={key} className="formulario-campo">
              <label className="formulario-label">{formatearLabel(key)}</label>

              {key==="genero" ? (
                <select name={key} value={formData[key]} onChange={handleChange} className="formulario-input">
                  <option value="">Seleccione...</option>
                  <option value="Masculino">Masculino</option>
                  <option value="Femenino">Femenino</option>
                  <option value="X">X</option>
                </select>
              ) : key==="estadoCivil" ? (
                <select name={key} value={formData[key]} onChange={handleChange} className="formulario-input">
                  <option value="">Seleccione...</option>
                  <option value="Soltero/a">Soltero/a</option>
                  <option value="Casado/a">Casado/a</option>
                  <option value="Divorciado/a">Divorciado/a</option>
                  <option value="Viudo/a">Viudo/a</option>
                  <option value="Concubinato">Concubinato</option>
                </select>
              ) : key==="resideProvincia" || key==="argentinoNativo" || key==="tieneBoletoEstudiantil" ? (
                <select name={key} value={formData[key]} onChange={handleChange} className="formulario-input">
                  <option value="">Seleccione...</option>
                  <option value="Si">Sí</option>
                  <option value="No">No</option>
                </select>
              ) : key==="noResideProvincia" || key==="provinciaNacimiento" ? (
                <select name={key} value={formData[key]} onChange={handleChange} className="formulario-input">
                  <option value="">Seleccione provincia...</option>
                  {PROVINCIAS.map(prov=><option key={prov} value={prov}>{prov}</option>)}
                </select>
              ) : key==="departamento" ? (
                <select name={key} value={formData[key]} onChange={handleChange} className="formulario-input">
                  <option value="">Seleccione departamento...</option>
                  {DEPARTAMENTOS_SF.map(dep=><option key={dep} value={dep}>{dep}</option>)}
                </select>
              ) : key==="nivelEstudio" ? (
                <select name={key} value={formData[key]} onChange={handleChange} className="formulario-input">
                  <option value="">Seleccione nivel...</option>
                  <option value="Secundario completo">Secundario completo</option>
                  <option value="Terciario técnico">Terciario técnico</option>
                  <option value="Terciario superior">Terciario superior</option>
                  <option value="Grado universitario">Grado universitario</option>
                  <option value="Posgrado">Posgrado</option>
                </select>
              ) : key.startsWith("pregunta_") ? (
                <select name={key} value={formData[key]} onChange={handleChange} className="formulario-input">
                  <option value="No">No</option>
                  <option value="Si">Sí</option>
                </select>
              ) : (
                <input
                  type={key.toLowerCase().includes("fecha")?"date":key==="dni"?"number":"text"}
                  name={key}
                  value={formData[key]}
                  onChange={handleChange}
                  className="formulario-input"
                  readOnly={key==="cuil"}
                />
              )}
            </div>
          );
        })}

        {etapaActual === etapas.length - 1 && (
          <div className="formulario-resumen">
            <h3>📋 Resumen de datos</h3>
            <table className="resumen-table">
              <tbody>
                {Object.entries(formData)
                  .filter(([key, value]) => value && value.trim() !== "")
                  .map(([key, value]) => (
                    <tr key={key}>
                      <td><strong>{formatearLabel(key)}</strong></td>
                      <td>{value}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="formulario-boton-container">
          {etapaActual>0 && <button type="button" onClick={handleBack} className="formulario-boton secundario">Retroceder</button>}
          {etapaActual < etapas.length -1 ? 
            <button type="button" onClick={handleNext} className="formulario-boton">Avanzar</button> :
            <button type="button" onClick={handleFinalizar} className="formulario-boton">Finalizar inscripción</button>
          }
        </div>
      </form>
    </div>
  );
};

export default FormularioProceso;
